import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import {
  BookOpen, Loader2, Sparkles, Check, RotateCcw, Save,
  Image as ImageIcon, Video, ExternalLink, ThumbsUp,
  Plus, Trash2, X, Upload, Eye, FileText,
  ChevronDown, ChevronUp, Maximize2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cleanCourseText } from '@/utils/textClean'
import { extractFileText } from '@/utils/extractFileText'

export interface SyllabusLesson {
  lesson_id: number
  title: string
  learning_objective: string
  passing_score: number
  core_concepts: string[]
}

export interface SyllabusModule {
  module_id: number
  title: string
  lessons: SyllabusLesson[]
}

type ConceptImageMap = Record<string, { url: string; caption: string; media_type?: 'image' | 'video' } | null>

function getYouTubeEmbedUrl(rawUrl: string): string | null {
  if (!rawUrl) return null
  const trimmed = rawUrl.trim()
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = trimmed.match(regExp)
  return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null
}

function getYouTubeThumbnail(rawUrl: string): string | null {
  if (!rawUrl) return null
  const trimmed = rawUrl.trim()
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = trimmed.match(regExp)
  return (match && match[2].length === 11) ? `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg` : null
}

function cleanModulePlan(plan: SyllabusModule[] | null | undefined): SyllabusModule[] | null {
  if (!plan) return null
  return plan.map((mod) => ({
    ...mod,
    title: cleanCourseText(mod.title),
    lessons: (mod.lessons || []).map((les) => ({
      ...les,
      title: cleanCourseText(les.title),
      learning_objective: cleanCourseText(les.learning_objective),
      core_concepts: (les.core_concepts || []).map((c) => cleanCourseText(c)),
    })),
  }))
}

interface MediaSuggestion {
  concept: string
  media_type: 'image' | 'video'
  url: string
  full_url?: string
  caption: string
  attribution: string
  source: string
  approved?: boolean
}

interface YouTubeQuery {
  concept: string
  media_type: 'video'
  suggested_search: string
  search_url: string
  source: string
}

export interface SyllabusGeneratorProps {
  classId: string
  targetAge: number | null
  subjectName: string
  existingPlan: SyllabusModule[] | null
  onSyllabusSaved: (plan: SyllabusModule[]) => void
  /** 'syllabus' = editable plan view. 'media' = per-lesson media manager. */
  mode?: 'syllabus' | 'media'
  /** Media-tab deep link: switches the parent ClassDetail to the Syllabus tab */
  onGoToSyllabus?: () => void
  initialUseSearch?: boolean
}

// ─── Concept Image Modal ──────────────────────────────────────────────────────
interface ConceptImageModalProps {
  classId?: string
  concept: string
  existing: { url: string; caption: string; media_type?: 'image' | 'video' } | null
  onSave: (url: string, caption: string, mediaType: 'image' | 'video') => void
  onClose: () => void
}

function ConceptImageModal({ classId, concept, existing, onSave, onClose }: ConceptImageModalProps) {
  const [tab, setTab] = useState<'upload' | 'url' | 'video'>(existing?.media_type === 'video' ? 'video' : 'upload')
  const [mediaType, setMediaType] = useState<'image' | 'video'>(existing?.media_type || 'image')
  const [url, setUrl] = useState(existing?.url || '')
  const [caption, setCaption] = useState(existing?.caption || '')
  const [previewOk, setPreviewOk] = useState(!!existing?.url)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleImageFile = async (file: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file (.png, .jpg, .jpeg, .webp, .gif).')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setUploadError('Image is too large. Maximum size is 15MB.')
      return
    }

    setIsUploading(true)
    setUploadError(null)

    try {
      const cleanExt = file.name.split('.').pop()?.toLowerCase() || 'png'
      const safeBase = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40)
      const folder = classId || 'general'
      const filePath = `${folder}/concept-images/${Date.now()}_${safeBase}.${cleanExt}`

      const { error: uploadErr } = await supabase.storage
        .from('class-knowledge')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type || 'image/png',
        })

      if (uploadErr) {
        console.error('[ConceptImageModal] upload error:', uploadErr)
        throw new Error(uploadErr.message || 'Storage upload failed.')
      }

      const { data: urlData } = supabase.storage
        .from('class-knowledge')
        .getPublicUrl(filePath)

      if (!urlData?.publicUrl) throw new Error('Could not retrieve public URL for uploaded photo.')

      setUrl(urlData.publicUrl)
      setPreviewOk(true)
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload photo. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const isValidUrl = (val: string) => {
    const trimmed = val.trim()
    return trimmed.startsWith('http://') || trimmed.startsWith('https://')
  }

  const canAttach = !isUploading && isValidUrl(url)

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && !isUploading && onClose()}
    >
      <div style={{
        background: '#0d1527', borderRadius: 20,
        border: '1px solid rgba(14, 165, 233, 0.3)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        width: '100%', maxWidth: 500, padding: 26,
        display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <h4 style={{ margin: 0, color: '#f8fafc', fontWeight: 800, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ImageIcon size={18} color="#38bdf8" /> Attach Concept Visual
            </h4>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.4, maxHeight: 42, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Concept: <span style={{ color: '#38bdf8', fontWeight: 700 }}>{concept}</span>
            </p>
          </div>
          <button onClick={onClose} disabled={isUploading} style={{ background: 'none', border: 'none', color: '#64748b', cursor: isUploading ? 'not-allowed' : 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Tab switcher: Upload vs URL vs Video */}
        <div style={{ display: 'flex', gap: 6, background: 'rgba(15,23,42,0.6)', padding: 4, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            type="button"
            onClick={() => { setTab('upload'); setMediaType('image') }}
            style={{
              flex: 1, padding: '8px 10px', borderRadius: 9, border: 'none',
              background: tab === 'upload' ? 'rgba(14,165,233,0.18)' : 'transparent',
              color: tab === 'upload' ? '#38bdf8' : '#94a3b8',
              fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            }}
          >
            <Upload size={13} /> Photo / GIF
          </button>
          <button
            type="button"
            onClick={() => { setTab('url'); setMediaType('image') }}
            style={{
              flex: 1, padding: '8px 10px', borderRadius: 9, border: 'none',
              background: tab === 'url' ? 'rgba(14,165,233,0.18)' : 'transparent',
              color: tab === 'url' ? '#38bdf8' : '#94a3b8',
              fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            }}
          >
            <ExternalLink size={13} /> Image/GIF URL
          </button>
          <button
            type="button"
            onClick={() => { setTab('video'); setMediaType('video') }}
            style={{
              flex: 1, padding: '8px 10px', borderRadius: 9, border: 'none',
              background: tab === 'video' ? 'rgba(239,68,68,0.18)' : 'transparent',
              color: tab === 'video' ? '#f87171' : '#94a3b8',
              fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            }}
          >
            <Video size={13} /> Video / YouTube
          </button>
        </div>

        {/* Upload Mode */}
        {tab === 'upload' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.png,.jpg,.jpeg,.webp,.gif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) handleImageFile(f)
                e.target.value = ''
              }}
            />

            {!url ? (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault(); setDragOver(false)
                  const f = e.dataTransfer.files?.[0]
                  if (f) handleImageFile(f)
                }}
                onClick={() => !isUploading && fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? '#38bdf8' : 'rgba(14, 165, 233, 0.35)'}`,
                  borderRadius: 14,
                  background: dragOver ? 'rgba(14,165,233,0.08)' : 'rgba(15,23,42,0.7)',
                  padding: '24px 16px',
                  textAlign: 'center',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s ease',
                }}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={26} className="animate-spin" color="#38bdf8" />
                    <p style={{ margin: 0, fontSize: 13, color: '#f8fafc', fontWeight: 700 }}>
                      Uploading photo to course storage…
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: '#64748b' }}>
                      Optimising and attaching to concept
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(14,165,233,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Upload size={20} color="#38bdf8" />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, color: '#f8fafc', fontWeight: 700 }}>
                        Click to choose photo or drag &amp; drop
                      </p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: '#64748b' }}>
                        PNG, JPG, WEBP, or GIF up to 15MB
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(14,165,233,0.3)', background: '#0f172a' }}>
                <img
                  src={url}
                  alt="concept preview"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'contain', background: 'rgba(0,0,0,0.4)', display: 'block' }}
                  onError={() => setPreviewOk(false)}
                />
                <div style={{ padding: '8px 12px', background: 'rgba(15,23,42,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: '#34d399', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Check size={14} /> Photo ready
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#f8fafc', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Change photo
                    </button>
                    <button
                      type="button"
                      onClick={() => { setUrl(''); setPreviewOk(false) }}
                      style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)', color: '#f87171', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}

            {uploadError && (
              <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 12 }}>
                {uploadError}
              </div>
            )}
          </div>
        )}

        {/* URL Mode */}
        {tab === 'url' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Image or GIF Web Address
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); setPreviewOk(false); setMediaType('image') }}
                  placeholder="https://example.com/diagram.jpg or .gif"
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.8)', color: '#f8fafc', fontSize: 13, outline: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => setPreviewOk(true)}
                  disabled={!isValidUrl(url)}
                  style={{
                    padding: '10px 14px', borderRadius: 10, border: 'none',
                    background: isValidUrl(url) ? '#0ea5e9' : 'rgba(255,255,255,0.05)',
                    color: isValidUrl(url) ? '#fff' : '#64748b',
                    cursor: isValidUrl(url) ? 'pointer' : 'not-allowed',
                    fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                  }}
                >
                  <Eye size={14} /> Preview
                </button>
              </div>
              <p style={{ margin: 0, fontSize: 11, color: '#64748b' }}>Must start with https://</p>
            </div>

            {previewOk && isValidUrl(url) && (
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(14,165,233,0.3)', background: '#0f172a' }}>
                <img
                  src={url}
                  alt="concept preview"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'contain', background: 'rgba(0,0,0,0.4)', display: 'block' }}
                  onError={() => setPreviewOk(false)}
                />
              </div>
            )}
          </div>
        )}

        {/* Video Mode */}
        {tab === 'video' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                YouTube or Video Web Address
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); setPreviewOk(false); setMediaType('video') }}
                  placeholder="https://www.youtube.com/watch?v=... or youtu.be/..."
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.8)', color: '#f8fafc', fontSize: 13, outline: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => setPreviewOk(true)}
                  disabled={!isValidUrl(url)}
                  style={{
                    padding: '10px 14px', borderRadius: 10, border: 'none',
                    background: isValidUrl(url) ? '#ef4444' : 'rgba(255,255,255,0.05)',
                    color: isValidUrl(url) ? '#fff' : '#64748b',
                    cursor: isValidUrl(url) ? 'pointer' : 'not-allowed',
                    fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                  }}
                >
                  <Eye size={14} /> Preview
                </button>
              </div>
              <p style={{ margin: 0, fontSize: 11, color: '#64748b' }}>Supports YouTube links or direct MP4 video URLs for micro-demonstrations</p>
            </div>

            {previewOk && isValidUrl(url) && (
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(239,68,68,0.3)', background: '#0f172a' }}>
                {getYouTubeEmbedUrl(url) ? (
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                    <iframe
                      src={getYouTubeEmbedUrl(url)!}
                      title="YouTube concept preview"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <video
                    controls
                    src={url}
                    style={{ width: '100%', maxHeight: 200, display: 'block', background: '#000' }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Caption */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Caption <span style={{ opacity: 0.5, fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. Espresso machine pressure gauge readout"
            style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.8)', color: '#f8fafc', fontSize: 13, outline: 'none' }}
          />
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
          {existing && (
            <button
              type="button"
              onClick={() => onSave('', '', 'image')}
              style={{ padding: '11px 16px', borderRadius: 12, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)', color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            style={{ flex: 1, padding: '11px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', fontWeight: 700, fontSize: 13, cursor: isUploading ? 'not-allowed' : 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(url.trim(), caption.trim(), mediaType)}
            disabled={!canAttach}
            style={{
              flex: 2, padding: '11px 16px', borderRadius: 12, border: 'none',
              background: canAttach
                ? (mediaType === 'video' ? 'linear-gradient(135deg, #ef4444, #f97316)' : 'linear-gradient(135deg, #0284c7, #0ea5e9)')
                : 'rgba(255,255,255,0.05)',
              color: canAttach ? '#fff' : '#64748b',
              fontWeight: 800, fontSize: 13,
              cursor: canAttach ? 'pointer' : 'not-allowed',
              boxShadow: canAttach ? (mediaType === 'video' ? '0 4px 14px rgba(239,68,68,0.3)' : '0 4px 14px rgba(14,165,233,0.3)') : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {mediaType === 'video' ? <Video size={16} /> : <Check size={16} />}
            {mediaType === 'video' ? 'Attach Video to Concept' : 'Attach Visual to Concept'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SyllabusGenerator({
  classId, targetAge, subjectName, existingPlan, onSyllabusSaved, mode = 'syllabus', onGoToSyllabus, initialUseSearch = false,
}: SyllabusGeneratorProps) {
  const { t } = useTranslation()
  const [prompt, setPrompt] = useState('')
  // Opt-in live web research (generate-syllabus v3 use_search)
  const [useSearch, setUseSearch] = useState(initialUseSearch)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [generatedPlan, setGeneratedPlan] = useState<SyllabusModule[] | null>(existingPlan)
  const [discardedPlan, setDiscardedPlan] = useState<SyllabusModule[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(!!existingPlan)
  const [activeDrawerLesson, setActiveDrawerLesson] = useState<{ mIdx: number; lIdx: number } | null>(null)

  // Per-concept image state — key = "classId::modId-lesId-cIdx"
  const [conceptImages, setConceptImages] = useState<ConceptImageMap>({})
  // Lesson-level approved media (concept IS NULL) — the full-class scan
  const [lessonGallery, setLessonGallery] = useState<{ id: string; media_type: string; url: string; caption: string | null; lesson_unit: number | null; lesson_number: number | null }[]>([])
  const [imageModal, setImageModal] = useState<{ key: string; concept: string } | null>(null)
  // Legacy lesson-level media
  const [mediaSuggestions, setMediaSuggestions] = useState<Record<string, MediaSuggestion[]>>({})
  const [youtubeQueries, setYoutubeQueries] = useState<Record<string, YouTubeQuery[]>>({})
  const [loadingMedia, setLoadingMedia] = useState<string | null>(null)
  const [savingMedia, setSavingMedia] = useState(false)
  const [mediaSaved, setMediaSaved] = useState(false)
  const [manualUrls, setManualUrls] = useState<Record<string, string>>({})
  const [manualImageUrls, setManualImageUrls] = useState<Record<string, string>>({})
  const [manualImageCaptions, setManualImageCaptions] = useState<Record<string, string>>({})

  // ── Course knowledge — SAME table the teaching engine reads ──
  const [knowledgeDocs, setKnowledgeDocs] = useState<{ id: string; title: string | null; file_name: string; char_count: number | null; extracted_text: string | null }[]>([])
  const [knowledgeTitle, setKnowledgeTitle] = useState('')
  const [knowledgeText, setKnowledgeText] = useState('')
  const [knowledgeBusy, setKnowledgeBusy] = useState(false)
  const [knowledgeMsg, setKnowledgeMsg] = useState<string | null>(null)

  // ── Rich taxonomy — resolve root → department → topic for this class ──
  const [taxonomy, setTaxonomy] = useState<{ root: string | null; department: string | null; topic: string | null } | null>(null)
  // Set-dedupe keeps dept-level matches clean: "Business & Workplace Topics → IT & Digital Tools"
  // instead of repeating the root twice when the category IS a department.
  const taxonomyPath = taxonomy
    ? [...new Set([taxonomy.root, taxonomy.department, taxonomy.topic].filter(Boolean))].join(' → ')
    : ''

  useEffect(() => {
    setGeneratedPlan(cleanModulePlan(existingPlan))
    setSaved(!!existingPlan)
  }, [existingPlan])

  // Walk categories.parent_id (max 3 levels: topic → department → root).
  // Falls back to classes.subject_group + the subjectName prop when the class
  // has no category_id (legacy classes) or the chain is incomplete.
  useEffect(() => {
    if (!classId) return
    let alive = true
    ;(async () => {
      try {
        const { data: cls } = await (supabase.from('classes') as any)
          .select('category_id, subject_group, description')
          .eq('id', classId)
          .single()
        // Pre-seed the prompt with the creation-time description so the
        // teacher's own words are never lost.
        if (cls?.description?.trim()) setPrompt(prev => prev.trim() ? prev : cls.description.trim())
        if (!alive) return
        if (!cls?.category_id) {
          setTaxonomy(cls?.subject_group ? { root: cls.subject_group, department: null, topic: subjectName } : null)
          return
        }
        const { data: topic } = await (supabase.from('categories') as any)
          .select('name, parent_id')
          .eq('id', cls.category_id)
          .single()
        if (!alive) return
        let department: string | null = null
        let root: string | null = null
        if (topic?.parent_id) {
          const { data: dept } = await (supabase.from('categories') as any)
            .select('name, parent_id')
            .eq('id', topic.parent_id)
            .single()
          if (!alive) return
          department = dept?.name ?? null
          if (dept?.parent_id) {
            const { data: rootNode } = await (supabase.from('categories') as any)
              .select('name')
              .eq('id', dept.parent_id)
              .single()
            if (!alive) return
            root = rootNode?.name ?? null
          }
        }
        if (!root && cls?.subject_group) root = cls.subject_group
        setTaxonomy({ root, department, topic: topic?.name || subjectName })
      } catch {
        if (alive) setTaxonomy(null)
      }
    })()
    return () => { alive = false }
  }, [classId, subjectName])

  // Load course knowledge — same rows memory-loader reads at teach time.
  useEffect(() => {
    if (!classId) return
    supabase
      .from('class_knowledge' as any)
      .select('id, title, file_name, extracted_text, char_count, file_url')
      .eq('class_id', classId)
      .eq('is_active', true)
      .order('created_at', { ascending: true })
      .then(({ data }: any) => { if (data) setKnowledgeDocs(data) })
  }, [classId])

  // Load ALL saved media: concept-attached images (keyed) + lesson-level
  // approved media (AI-suggested / teacher-added) for the Media-tab scan.
  useEffect(() => {
    if (!classId) return
    supabase
      .from('lesson_media')
      .select('*')
      .eq('class_id', classId)
      .then(({ data }) => {
        if (!data) return
        const map: ConceptImageMap = {}
        const lesson: { id: string; media_type: string; url: string; caption: string | null; lesson_unit: number | null; lesson_number: number | null }[] = []
        for (const row of data as any[]) {
          if (row.concept && row.url) map[row.concept] = { url: row.url, caption: row.caption || '', media_type: row.media_type || 'image' }
          else if (!row.concept && row.approved === true && row.url) lesson.push({ id: row.id, media_type: row.media_type, url: row.url, caption: row.caption, lesson_unit: row.lesson_unit, lesson_number: row.lesson_number })
        }
        setConceptImages(map)
        setLessonGallery(lesson)
      })
  }, [classId])

  // ── Plan mutation helpers ────────────────────────────────────────────────────
  const updatePassingScore = (mIdx: number, lIdx: number, newScore: string) => {
    if (!generatedPlan) return
    const score = Math.min(100, Math.max(0, parseInt(newScore) || 0))
    const p = [...generatedPlan]; p[mIdx].lessons[lIdx].passing_score = score
    setGeneratedPlan(p); setSaved(false)
  }
  const updateModuleTitle = (mIdx: number, value: string) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]; p[mIdx] = { ...p[mIdx], title: value }
    setGeneratedPlan(p); setSaved(false)
  }
  const updateConceptField = (mIdx: number, lIdx: number, cIdx: number, value: string) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]
    const c = [...p[mIdx].lessons[lIdx].core_concepts]; c[cIdx] = value
    p[mIdx].lessons[lIdx] = { ...p[mIdx].lessons[lIdx], core_concepts: c }
    setGeneratedPlan(p); setSaved(false)
  }
  const addConcept = (mIdx: number, lIdx: number) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]
    p[mIdx].lessons[lIdx] = { ...p[mIdx].lessons[lIdx], core_concepts: [...p[mIdx].lessons[lIdx].core_concepts, ''] }
    setGeneratedPlan(p); setSaved(false)
  }
  const removeConcept = (mIdx: number, lIdx: number, cIdx: number) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]
    p[mIdx].lessons[lIdx] = { ...p[mIdx].lessons[lIdx], core_concepts: p[mIdx].lessons[lIdx].core_concepts.filter((_, i) => i !== cIdx) }
    setGeneratedPlan(p); setSaved(false)
  }
  const addLesson = (mIdx: number) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]
    const nextLessonId = Math.max(0, ...p[mIdx].lessons.map(l => l.lesson_id)) + 1
    const newLIdx = p[mIdx].lessons.length
    p[mIdx] = { ...p[mIdx], lessons: [...p[mIdx].lessons, { lesson_id: nextLessonId, title: '', learning_objective: '', passing_score: 80, core_concepts: [] }] }
    setGeneratedPlan(p); setSaved(false)
    setActiveDrawerLesson({ mIdx, lIdx: newLIdx })
  }
  const addModule = () => {
    if (!generatedPlan) return
    const newMIdx = generatedPlan.length
    setGeneratedPlan([...generatedPlan, { module_id: Math.max(0, ...generatedPlan.map(m => m.module_id)) + 1, title: '', lessons: [{ lesson_id: 1, title: '', learning_objective: '', passing_score: 80, core_concepts: [] }] }])
    setSaved(false)
    setActiveDrawerLesson({ mIdx: newMIdx, lIdx: 0 })
  }
  const deleteModule = (mIdx: number) => {
    if (!generatedPlan) return
    setGeneratedPlan(generatedPlan.filter((_, i) => i !== mIdx)); setSaved(false)
    if (activeDrawerLesson?.mIdx === mIdx) setActiveDrawerLesson(null)
  }
  const deleteLesson = (mIdx: number, lIdx: number) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]; p[mIdx].lessons = p[mIdx].lessons.filter((_, i) => i !== lIdx)
    setGeneratedPlan(p); setSaved(false)
    if (activeDrawerLesson?.mIdx === mIdx && activeDrawerLesson?.lIdx === lIdx) setActiveDrawerLesson(null)
  }
  const updateLessonField = (mIdx: number, lIdx: number, field: 'title' | 'learning_objective', value: string) => {
    if (!generatedPlan) return
    const p = [...generatedPlan]; p[mIdx].lessons[lIdx][field] = value
    setGeneratedPlan(p); setSaved(false)
  }

  // ── Generate & Save syllabus ────────────────────────────────────────────────
  const generateSyllabus = async () => {
    if (!prompt.trim()) return
    setIsLoading(true); setError(null)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('generate-syllabus', {
        body: {
          subject: taxonomy?.topic || subjectName,
          // Rich taxonomy context,  forwarded for when the edge fn is updated
          // to read it; the taxonomy preamble in teacher_request makes it work
          // with the CURRENT edge function unchanged.
          subject_department: taxonomy?.department || null,
          subject_root: taxonomy?.root || null,
          subject_path: taxonomyPath || null,
          target_age: targetAge || 14,
          teacher_request: prompt + (taxonomyPath ? `\n\n[Subject taxonomy: ${taxonomyPath}]` : ''),
          class_id: classId,
          use_search: useSearch,
        },
      })
      if (fnError) throw fnError
      if (data?.module_plan) { setGeneratedPlan(cleanModulePlan(data.module_plan)); setSaved(false) }
      else throw new Error('No module plan returned')
    } catch (err) { setError((err as Error).message || t('education.syllabus.error.generate')) }
    finally { setIsLoading(false) }
  }

  const saveToDatabase = async () => {
    if (!generatedPlan) return
    setIsSaving(true); setError(null)
    const cleaned = cleanModulePlan(generatedPlan) || generatedPlan
    setGeneratedPlan(cleaned)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: updateErr } = await supabase.from('classes').update({ module_plan: cleaned as any }).eq('id', classId)
      if (updateErr) throw updateErr
      setSaved(true); onSyllabusSaved(cleaned)
    } catch (err) { setError((err as Error).message || 'Failed to save') }
    finally { setIsSaving(false) }
  }

    // ── Course knowledge handlers — one store: class_knowledge ──
  const addKnowledgeDoc = async (title: string, content: string, filename?: string) => {
    const cleanTitle = title.trim() || filename?.replace(/\.[^.]+$/, '') || 'Untitled document'
    const cleanContent = content.trim()
    if (!cleanContent) return
    if (cleanContent.length > 200000) {
      setKnowledgeMsg('Document too large: max ~200,000 characters. Split it into parts.')
      return
    }
    setKnowledgeBusy(true); setKnowledgeMsg(null)
    try {
      const { data, error } = await (supabase.from('class_knowledge') as any)
        .insert({ class_id: classId, file_name: cleanTitle, file_url: null, extracted_text: cleanContent, title: cleanTitle, char_count: cleanContent.length, is_active: true })
        .select('id, title, file_name, extracted_text, char_count, file_url')
        .single()
      if (error) throw error
      setKnowledgeDocs(prev => [...prev, data as any])
      setKnowledgeTitle(''); setKnowledgeText('')
      setKnowledgeMsg('Added: generation and teaching will use it.')
    } catch (err: any) {
      setKnowledgeMsg(err.message || 'Failed to add document')
    } finally { setKnowledgeBusy(false) }
  }

  const deleteKnowledgeDoc = async (id: string) => {
    setKnowledgeBusy(true); setKnowledgeMsg(null)
    try {
      const { error } = await (supabase.from('class_knowledge') as any).delete().eq('id', id)
      if (error) throw error
      setKnowledgeDocs(prev => prev.filter(d => d.id !== id))
    } catch (err: any) {
      setKnowledgeMsg(err.message || 'Failed to delete document')
    } finally { setKnowledgeBusy(false) }
  }

  const handleKnowledgeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setKnowledgeBusy(true)
    setKnowledgeMsg(`Reading ${file.name}\u2026`)
    try {
      const text = await extractFileText(file)
      if (!text?.trim()) { setKnowledgeBusy(false); setKnowledgeMsg('That file appears to be empty or unreadable. Try pasting the text below.'); return }
      await addKnowledgeDoc(file.name.replace(/\.[^.]+$/, ''), text, file.name)
    } catch {
      setKnowledgeBusy(false)
      setKnowledgeMsg('Could not extract text from that file. Try pasting the text below.')
    }
  }

  // ── Concept image handlers ──────────────────────────────────────────────────
  const conceptKey = (modId: number, lesId: number, cIdx: number) =>
    `${classId}::${modId}-${lesId}-${cIdx}`

  // Media-tab deep link: flip the outer tab to Syllabus, then scroll to the pill
  const jumpToConcept = (key: string) => {
    onGoToSyllabus?.()
    const parts = key.replace(`${classId}::`, '').split('-')
    const [modId, lesId] = parts.map(Number)
    if (generatedPlan) {
      const mIdx = generatedPlan.findIndex(m => m.module_id === modId)
      if (mIdx !== -1) {
        const lIdx = generatedPlan[mIdx].lessons.findIndex(l => l.lesson_id === lesId)
        if (lIdx !== -1) {
          setActiveDrawerLesson({ mIdx, lIdx })
        }
      }
    }
    setTimeout(() => {
      document.getElementById(`concept-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 200)
  }

  const openImageModal = (modId: number, lesId: number, cIdx: number, concept: string) =>
    setImageModal({ key: conceptKey(modId, lesId, cIdx), concept })

  const saveConceptImage = useCallback(async (key: string, url: string, caption: string, mediaType: 'image' | 'video' = 'image') => {
    setImageModal(null)
    if (!url) {
      setConceptImages(prev => { const next = { ...prev }; delete next[key]; return next })
      try {
        await supabase.from('lesson_media').delete().eq('class_id', classId).eq('concept', key)
      } catch (e) {
        console.warn('Failed to delete concept image:', e)
      }
      return
    }
    setConceptImages(prev => ({ ...prev, [key]: { url, caption, media_type: mediaType } }))

    // Auto-save to database immediately so it persists without requiring the extra button click
    try {
      const parts = key.replace(`${classId}::`, '').split('-')
      const [modId, lesId] = parts.map(Number)
      await supabase.from('lesson_media').delete().eq('class_id', classId).eq('concept', key)
      const isYouTube = url.includes('youtube.com') || url.includes('youtu.be')
      const { error: insErr } = await supabase.from('lesson_media').insert({
        class_id: classId,
        lesson_unit: modId || 0,
        lesson_number: lesId || 0,
        concept: key,
        media_type: mediaType,
        url: url.trim(),
        caption: caption.trim() || '',
        attribution: 'Teacher',
        source: isYouTube ? 'youtube' : 'custom',
        approved: true,
      })
      if (insErr) throw insErr
      setError(null)
      onSyllabusSaved(generatedPlan || [])
    } catch (err: any) {
      console.warn('Auto-save of concept image failed:', err)
      setError(err?.message || 'Failed to save visual to concept.')
    }
  }, [classId, generatedPlan, onSyllabusSaved])

  // Direct lesson cover file upload helper
  const [uploadingCoverKey, setUploadingCoverKey] = useState<string | null>(null)
  const handleUploadLessonCover = async (key: string, file: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (.png, .jpg, .jpeg, .webp, .gif).')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('Image is too large. Maximum size is 15MB.')
      return
    }
    setUploadingCoverKey(key)
    setError(null)
    try {
      const cleanExt = file.name.split('.').pop()?.toLowerCase() || 'png'
      const safeBase = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40)
      const folder = classId || 'general'
      const filePath = `${folder}/lesson-covers/${Date.now()}_${safeBase}.${cleanExt}`

      const { error: uploadErr } = await supabase.storage
        .from('class-knowledge')
        .upload(filePath, file, { cacheControl: '3600', upsert: true, contentType: file.type || 'image/png' })
      if (uploadErr) throw uploadErr

      const { data: urlData } = supabase.storage.from('class-knowledge').getPublicUrl(filePath)
      if (!urlData?.publicUrl) throw new Error('Could not retrieve public URL.')

      const caption = manualImageCaptions[key]?.trim() || file.name.replace(/\.[^.]+$/, '')
      const [modId, lesId] = key.split('-').map(Number)

      const { data: insertRow, error: insErr } = await supabase
        .from('lesson_media')
        .insert({
          class_id: classId,
          lesson_unit: modId,
          lesson_number: lesId,
          media_type: 'image',
          url: urlData.publicUrl,
          caption,
          attribution: 'Teacher',
          source: 'upload',
          approved: true,
        })
        .select('id, media_type, url, caption, lesson_unit, lesson_number')
        .single()

      if (insErr) throw insErr

      setMediaSuggestions(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), { concept: 'Lesson cover', media_type: 'image', url: urlData.publicUrl, caption, attribution: 'Teacher', source: 'upload', approved: true }]
      }))
      if (insertRow) {
        setLessonGallery(prev => [insertRow as any, ...prev])
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload lesson cover image.')
    } finally {
      setUploadingCoverKey(null)
    }
  }

  // ── Legacy lesson-level media ────────────────────────────────────────────────
  const suggestMedia = async (module: SyllabusModule, lesson: SyllabusLesson) => {
    const key = `${module.module_id}-${lesson.lesson_id}`
    setLoadingMedia(key)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('suggest-lesson-media', {
        body: { core_concepts: lesson.core_concepts, lesson_title: lesson.title, target_age: targetAge || 14 },
      })
      if (fnErr) throw fnErr
      setMediaSuggestions(prev => ({ ...prev, [key]: (data?.suggestions || []).map((s: Partial<MediaSuggestion>) => ({ ...s, approved: false })) }))
      setYoutubeQueries(prev => ({ ...prev, [key]: data?.youtube_queries || [] }))
    } catch (err) { setError((err as Error).message || 'Failed to suggest media') }
    finally { setLoadingMedia(null) }
  }

  const suggestAllMedia = async () => {
    if (!generatedPlan) return
    setLoadingMedia('all')
    try {
      for (const mod of generatedPlan) {
        for (const les of mod.lessons) {
          const key = `${mod.module_id}-${les.lesson_id}`
          if (!mediaSuggestions[key] || mediaSuggestions[key].length === 0) await suggestMedia(mod, les)
        }
      }
    } finally { setLoadingMedia(null) }
  }

  const toggleApproval = (key: string, idx: number) => {
    setMediaSuggestions(prev => { const u = [...(prev[key] || [])]; u[idx] = { ...u[idx], approved: !u[idx].approved }; return { ...prev, [key]: u } })
    setMediaSaved(false)
  }
  const approveAll = (key: string) => {
    setMediaSuggestions(prev => ({ ...prev, [key]: (prev[key] || []).map(s => ({ ...s, approved: true })) }))
    setMediaSaved(false)
  }
  const removeMediaItem = (key: string, idx: number) => {
    setMediaSuggestions(prev => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }))
    setMediaSaved(false)
  }
  const addManualVideo = (key: string) => {
    const url = manualUrls[key]?.trim(); if (!url) return
    setMediaSuggestions(prev => ({ ...prev, [key]: [...(prev[key] || []), { concept: 'Teacher added', media_type: 'video' as const, url, caption: 'Teacher-selected video', attribution: 'YouTube', source: 'youtube', approved: true }] }))
    setManualUrls(prev => ({ ...prev, [key]: '' })); setMediaSaved(false)
  }
  const addManualImage = (key: string) => {
    const url = manualImageUrls[key]?.trim(); if (!url) return
    setMediaSuggestions(prev => ({ ...prev, [key]: [...(prev[key] || []), { concept: 'Teacher added', media_type: 'image' as const, url, caption: manualImageCaptions[key]?.trim() || 'Lesson visual', attribution: 'Custom', source: 'custom', approved: true }] }))
    setManualImageUrls(prev => ({ ...prev, [key]: '' })); setManualImageCaptions(prev => ({ ...prev, [key]: '' })); setMediaSaved(false)
  }

  const saveAllMedia = async () => {
    setSavingMedia(true); setError(null)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inserts: any[] = []
      for (const [key, suggestions] of Object.entries(mediaSuggestions)) {
        const [moduleId, lessonId] = key.split('-').map(Number)
        for (const s of suggestions) {
          if (s.approved) inserts.push({ class_id: classId, lesson_unit: moduleId, lesson_number: lessonId, media_type: s.media_type, url: s.url, caption: s.caption, attribution: s.attribution, source: s.source, approved: true })
        }
      }
      if (inserts.length > 0) { const { error: insertErr } = await supabase.from('lesson_media').insert(inserts); if (insertErr) throw insertErr }
      setMediaSaved(true); onSyllabusSaved(generatedPlan || [])
    } catch (err: any) { setError(err.message || 'Failed to save media') }
    finally { setSavingMedia(false) }
  }

  const totalLessons = generatedPlan ? generatedPlan.reduce((sum, m) => sum + m.lessons.length, 0) : 0
  const approvedCount = Object.values(mediaSuggestions).flat().filter(s => s.approved).length
  const conceptImageCount = Object.values(conceptImages).filter(Boolean).length

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: mode === 'media' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'linear-gradient(135deg, #0284c7, #0ea5e9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: mode === 'media' ? '0 4px 14px rgba(99,102,241,0.25)' : '0 4px 14px rgba(14,165,233,0.25)',
        }}>
          {mode === 'media' ? <ImageIcon size={20} color="#fff" /> : <Sparkles size={20} color="#fff" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontWeight: 800, color: '#f8fafc', margin: 0, fontSize: 'clamp(16px, 4vw, 18px)' }}>
            {mode === 'media' ? 'Course Media Manager' : t('education.syllabus.title')}
          </h3>
          <p style={{ fontSize: 'clamp(11px, 3.5vw, 13px)', color: '#94a3b8', margin: 0 }}>
            {mode === 'media'
              ? 'Manage images attached to individual concepts across all lessons'
              : t('education.syllabus.description')}
          </p>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* ════════════════════ SYLLABUS MODE ═════════════════════ */}
      {mode === 'syllabus' && (
        <>
          {/* ── Course knowledge — the org's own documents ── */}
          <div style={{ background: '#0d1527', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: 'clamp(12px, 3.5vw, 20px)', display: 'flex', flexDirection: 'column', gap: 14, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(14,165,233,0.12)', color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BookOpen size={17} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: 0, color: '#f8fafc', fontWeight: 800, fontSize: 15 }}>
                  Course knowledge {knowledgeDocs.length > 0 && <span style={{ color: '#38bdf8' }}>({knowledgeDocs.length})</span>}
                </h4>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                  Drop in your SOPs, recipes, or standards, the AI builds the course from YOUR material and teaches it your way.
                </p>
              </div>
            </div>

            {knowledgeDocs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {knowledgeDocs.map(d => {
                  const readable = !!d.extracted_text
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'rgba(15,23,42,0.6)', borderRadius: 10, border: `1px solid ${readable ? 'rgba(255,255,255,0.08)' : 'rgba(245,158,11,0.3)'}` }}>
                      <FileText size={14} color={readable ? '#38bdf8' : '#fbbf24'} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title || d.file_name}</p>
                        <p style={{ margin: 0, fontSize: 10.5, color: readable ? '#64748b' : '#fbbf24' }}>
                          {readable ? `${(d.char_count || d.extracted_text?.length || 0).toLocaleString()} characters` : 'No text, the AI can\'t read this file. Paste its text below.'}
                        </p>
                      </div>
                      <button onClick={() => deleteKnowledgeDoc(d.id)} disabled={knowledgeBusy} title="Remove" style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%' }}>
                <input
                  value={knowledgeTitle}
                  onChange={e => setKnowledgeTitle(e.target.value)}
                  placeholder="Document title (e.g. Espresso preparation SOP)"
                  style={{ flex: '1 1 180px', minWidth: 0, padding: '9px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.7)', color: '#f8fafc', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
                <label style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', borderRadius: 10, border: '1px dashed rgba(14,165,233,0.35)', color: '#38bdf8', fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                  <Upload size={13} /> Upload file (.txt .md .docx .pdf)
                  <input type="file" accept=".txt,.md,.docx,.pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf" style={{ display: 'none' }} onChange={handleKnowledgeFile} />
                </label>
              </div>
              <textarea value={knowledgeText} onChange={e => setKnowledgeText(e.target.value)} rows={3} placeholder="…or paste the document text here (open your Word/PDF doc, select all, copy, paste)" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.7)', color: '#f8fafc', fontSize: 12.5, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <button onClick={() => addKnowledgeDoc(knowledgeTitle, knowledgeText)} disabled={knowledgeBusy || !knowledgeText.trim()} style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: 'none', background: knowledgeText.trim() ? '#0ea5e9' : 'rgba(255,255,255,0.05)', color: knowledgeText.trim() ? '#fff' : '#64748b', fontWeight: 700, fontSize: 12, cursor: knowledgeText.trim() && !knowledgeBusy ? 'pointer' : 'not-allowed' }}>
                {knowledgeBusy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add document
              </button>
            </div>

            {knowledgeMsg && <p style={{ margin: 0, fontSize: 11.5, color: knowledgeMsg.startsWith('Added') ? '#34d399' : '#fbbf24' }}>{knowledgeMsg}</p>}
            <p style={{ margin: 0, fontSize: 10.5, color: '#475569', lineHeight: 1.5 }}>
              The first ~40,000 characters of your documents are included in every lesson; generation reads up to ~120,000.
            </p>
          </div>

          {/* Generation form, only when no plan */}
          {!generatedPlan && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {discardedPlan && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderRadius: 12, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                  <span style={{ fontSize: 13, color: '#fbbf24', fontWeight: 600 }}>You have a discarded draft, restore it?</span>
                  <button onClick={() => { setGeneratedPlan(discardedPlan); setDiscardedPlan(null); setSaved(false) }} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Restore</button>
                </div>
              )}
              {!prompt && (
                <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#38bdf8', fontWeight: 600 }}>
                    {taxonomyPath && (
                      <span style={{ display: 'block', marginBottom: 4, fontSize: 11, color: '#a78bfa', fontWeight: 700 }}>
                        {taxonomyPath}
                      </span>
                    )}
                    Tip: describe your goals,  e.g. <em>"{taxonomy?.topic || subjectName} for {targetAge ? `age ${targetAge}` : 'high school'},  12-week intro, emphasis on hands-on projects"</em>
                  </p>
                </div>
              )}
              <textarea
                placeholder={t('education.syllabus.placeholder')} value={prompt}
                onChange={(e) => setPrompt(e.target.value)} rows={4}
                style={{ width: '100%', padding: '14px 16px', borderRadius: 14, border: '1px solid rgba(255,255,255,0.12)', fontSize: 15, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', background: 'rgba(15,23,42,0.7)', color: '#f8fafc' }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={useSearch} onChange={(e) => setUseSearch(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#0ea5e9', cursor: 'pointer' }} />
                Use live web search for current facts, prices &amp; versions (adds ~20–30s)
              </label>
              <button
                onClick={generateSyllabus} disabled={!prompt.trim() || isLoading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 24px', background: 'linear-gradient(135deg, #0284c7, #0ea5e9)', color: '#fff', border: 'none', borderRadius: 14, fontWeight: 700, fontSize: 15, cursor: !prompt.trim() || isLoading ? 'not-allowed' : 'pointer', opacity: !prompt.trim() || isLoading ? 0.6 : 1, boxShadow: '0 4px 14px rgba(14,165,233,0.3)' }}
              >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {isLoading ? t('education.syllabus.generating') : t('education.syllabus.generate')}
              </button>
            </div>
          )}

          {/* Plan editor */}
          {generatedPlan && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
              {/* Status bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: saved ? 'rgba(16,185,129,0.12)' : 'rgba(14,165,233,0.12)', borderRadius: 12, border: `1px solid ${saved ? 'rgba(16,185,129,0.3)' : 'rgba(14,165,233,0.3)'}`, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: saved ? '#34d399' : '#38bdf8' }}>
                  {saved ? t('education.syllabus.status.published') : t('education.syllabus.status.draft')}
                  {' · '}{t('education.syllabus.status.units', { count: generatedPlan.length })}
                  {' · '}{t('education.syllabus.status.lessons', { count: totalLessons })}
                  {conceptImageCount > 0 && <span style={{ color: '#a78bfa' }}> · 🖼 {conceptImageCount} concept {conceptImageCount === 1 ? 'image' : 'images'}</span>}
                </span>
              </div>



              {/* Modules */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
                {generatedPlan.map((module, mIdx) => (
                  <div key={`mod-${mIdx}`} style={{ background: '#0d1527', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden', width: '100%', boxSizing: 'border-box', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                    {/* Module header */}
                    <div style={{ padding: '12px 14px', background: 'linear-gradient(135deg, #1e293b, #0f172a)', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 200px', minWidth: 0 }}>
                        <BookOpen size={16} color="#38bdf8" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#38bdf8', background: 'rgba(14,165,233,0.15)', padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>Unit {module.module_id}</span>
                        <input value={module.title} onChange={(e) => updateModuleTitle(mIdx, e.target.value)} style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', color: '#f8fafc', fontWeight: 800, fontSize: 15, outline: 'none', padding: '3px 6px', minWidth: 0 }} placeholder="Module title" />
                      </div>
                      <button onClick={() => deleteModule(mIdx)} style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#f87171', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <Trash2 size={12} /> Delete Unit
                      </button>
                    </div>

                    {/* Lessons */}
                    <div style={{ padding: 'clamp(10px, 3vw, 16px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {module.lessons.map((lesson, lIdx) => (
                        <div key={`les-${lIdx}`} style={{ background: '#131e33', borderRadius: 14, padding: 'clamp(12px, 3.5vw, 18px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', width: '100%', boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', width: '100%' }}>
                            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                              {/* Title row */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                <input value={lesson.title} onChange={(e) => updateLessonField(mIdx, lIdx, 'title', e.target.value)} style={{ flex: '1 1 180px', border: 'none', background: 'transparent', fontWeight: 800, fontSize: 16, color: '#f8fafc', margin: 0, padding: '4px 0', outline: 'none', minWidth: 0 }} placeholder={t('education.syllabus.editor.lessonPlaceholder')} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                  <button
                                    type="button"
                                    onClick={() => setActiveDrawerLesson({ mIdx, lIdx })}
                                    title="Open full lesson detail drawer"
                                    style={{
                                      background: 'rgba(14,165,233,0.12)',
                                      border: '1px solid rgba(14,165,233,0.3)',
                                      borderRadius: 8,
                                      color: '#38bdf8',
                                      padding: '4px 8px',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    <Maximize2 size={12} />
                                    <span>Full View</span>
                                  </button>
                                  <button onClick={() => deleteLesson(mIdx, lIdx)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4, flexShrink: 0 }} title="Delete lesson"><Trash2 size={15} /></button>
                                </div>
                              </div>
                              {/* Objective */}
                              <textarea value={lesson.learning_objective} onChange={(e) => updateLessonField(mIdx, lIdx, 'learning_objective', e.target.value)} rows={2} style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, color: '#94a3b8', lineHeight: 1.5, resize: 'none', outline: 'none', padding: 0, margin: '0 0 14px', boxSizing: 'border-box' }} placeholder={t('education.syllabus.editor.objectivePlaceholder')} />
                              {/* Concept pills */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-start', width: '100%' }}>
                                {lesson.core_concepts.map((c, cIdx) => {
                                  const imgKey = conceptKey(module.module_id, lesson.lesson_id, cIdx)
                                  const visual = conceptImages[imgKey]
                                  const hasVisual = !!visual
                                  const isVid = visual?.media_type === 'video'
                                  return (
                                    <div
                                      key={cIdx}
                                      id={`concept-${imgKey}`}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center',
                                        background: hasVisual ? (isVid ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.15)') : 'rgba(14,165,233,0.1)',
                                        borderRadius: 20,
                                        border: `1px solid ${hasVisual ? (isVid ? 'rgba(239,68,68,0.45)' : 'rgba(139,92,246,0.4)') : 'rgba(14,165,233,0.28)'}`,
                                        overflow: 'hidden',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box'
                                      }}
                                    >
                                      {/* Editable text, capped so it never blows out mobile width */}
                                      <input
                                        value={c}
                                        onChange={(e) => updateConceptField(mIdx, lIdx, cIdx, e.target.value)}
                                        title={c}
                                        style={{
                                          fontSize: 11, background: 'transparent', border: 'none', outline: 'none',
                                          color: hasVisual ? (isVid ? '#f87171' : '#a78bfa') : '#38bdf8',
                                          fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
                                          padding: '4px 6px 4px 10px',
                                          width: `min(${Math.max(40, c.length * 7)}px, calc(100vw - 140px))`,
                                          maxWidth: 'calc(100% - 46px)',
                                          minWidth: '40px',
                                          boxSizing: 'border-box'
                                        }}
                                      />
                                      {/* Attach visual */}
                                      <button
                                        onClick={() => openImageModal(module.module_id, lesson.lesson_id, cIdx, c)}
                                        title={hasVisual ? (isVid ? 'Video attached — click to edit' : 'Visual attached — click to edit') : 'Attach photo, GIF or video to this concept'}
                                        style={{ background: hasVisual ? (isVid ? 'rgba(239,68,68,0.25)' : 'rgba(139,92,246,0.25)') : 'rgba(14,165,233,0.15)', border: 'none', cursor: 'pointer', padding: '4px 6px', lineHeight: 1, display: 'flex', alignItems: 'center', color: hasVisual ? (isVid ? '#f87171' : '#a78bfa') : '#38bdf8', flexShrink: 0 }}
                                      >
                                        {hasVisual ? (isVid ? <Video size={11} strokeWidth={2.5} /> : <Check size={10} strokeWidth={3} />) : <Plus size={10} strokeWidth={3} />}
                                      </button>
                                      {/* Remove concept */}
                                      <button onClick={() => removeConcept(mIdx, lIdx, cIdx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px 8px 4px 2px', lineHeight: 1, fontSize: 13, fontWeight: 900, display: 'flex', alignItems: 'center', flexShrink: 0 }} title="Remove concept">×</button>
                                    </div>
                                  )
                                })}
                                <button onClick={() => addConcept(mIdx, lIdx)} style={{ fontSize: 10, background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(14,165,233,0.35)', color: '#38bdf8', borderRadius: 20, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>+ concept</button>
                                {lesson.core_concepts.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setActiveDrawerLesson({ mIdx, lIdx })}
                                    title="Open all concepts in full drawer"
                                    style={{
                                      fontSize: 10,
                                      background: 'rgba(14,165,233,0.08)',
                                      border: '1px solid rgba(14,165,233,0.22)',
                                      color: '#38bdf8',
                                      borderRadius: 20,
                                      padding: '4px 10px',
                                      cursor: 'pointer',
                                      fontWeight: 700,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      flexShrink: 0
                                    }}
                                  >
                                    <Maximize2 size={10} /> Read all {lesson.core_concepts.length} full
                                  </button>
                                )}
                              </div>
                              {/* Image hint */}
                              {lesson.core_concepts.length > 0 && (
                                <div style={{ marginTop: 10 }}>
                                  {(() => {
                                    const n = lesson.core_concepts.filter((_, ci) => !!conceptImages[conceptKey(module.module_id, lesson.lesson_id, ci)]).length
                                    if (n === 0) {
                                      return (
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '6px 10px',
                                            background: 'rgba(14, 165, 233, 0.05)',
                                            border: '1px solid rgba(14, 165, 233, 0.16)',
                                            borderRadius: 8,
                                            fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 9.5,
                                              fontWeight: 800,
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.06em',
                                              color: '#0ea5e9',
                                              background: 'rgba(14, 165, 233, 0.14)',
                                              padding: '2px 6px',
                                              borderRadius: 4,
                                              flexShrink: 0
                                            }}
                                          >
                                            Tip
                                          </span>
                                          <span style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.35, fontWeight: 500 }}>
                                            Click <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: 3, background: 'rgba(14, 165, 233, 0.22)', color: '#38bdf8', verticalAlign: 'middle', margin: '0 2px' }}><Plus size={9} strokeWidth={3} /></span> on a concept pill to attach photo, GIF, or video
                                          </span>
                                        </div>
                                      )
                                    }
                                    return (
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 7,
                                          padding: '6px 10px',
                                          background: 'rgba(167, 139, 250, 0.08)',
                                          border: '1px solid rgba(167, 139, 250, 0.24)',
                                          borderRadius: 8,
                                          fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
                                        }}
                                      >
                                        <Sparkles size={12} color="#a78bfa" style={{ flexShrink: 0 }} />
                                        <span style={{ fontSize: 11.5, color: '#c4b5fd', fontWeight: 600, lineHeight: 1.35 }}>
                                          {n} of {lesson.core_concepts.length} concepts have visuals attached
                                        </span>
                                      </div>
                                    )
                                  })()}
                                </div>
                              )}
                            </div>
                            {/* Passing score */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'auto', padding: '8px 12px', background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, alignSelf: 'flex-start' }}>
                              <label style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>{t('education.syllabus.editor.passingLabel')}</label>
                              <input type="number" min="0" max="100" value={lesson.passing_score} onChange={(e) => updatePassingScore(mIdx, lIdx, e.target.value)} style={{ width: 50, height: 32, textAlign: 'center', borderRadius: 8, border: '1px solid rgba(14,165,233,0.3)', background: '#0b1120', color: '#38bdf8', fontSize: 15, fontWeight: 800, outline: 'none' }} />
                            </div>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => addLesson(mIdx)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', background: 'rgba(255,255,255,0.02)', color: '#94a3b8', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: 12, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}><Plus size={14} /> Add lesson</button>
                    </div>
                  </div>
                ))}
                <button onClick={addModule} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '14px', background: 'rgba(255,255,255,0.03)', color: '#38bdf8', border: '1px dashed rgba(14,165,233,0.35)', borderRadius: 16, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}><Plus size={16} /> Add module</button>
              </div>

              {/* Bottom actions */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button onClick={() => { setDiscardedPlan(generatedPlan); setGeneratedPlan(null); setSaved(false); setPrompt('') }} style={{ flex: '1 1 180px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  <RotateCcw size={16} /> {t('education.syllabus.editor.discard')}
                </button>
                <button onClick={saveToDatabase} disabled={isSaving || saved} style={{ flex: '1 1 180px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px', background: saved ? '#059669' : 'linear-gradient(135deg, #0284c7, #0ea5e9)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: isSaving || saved ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.6 : 1, boxShadow: '0 4px 14px rgba(14,165,233,0.3)' }}>
                  {isSaving ? <Loader2 size={16} className="animate-spin" /> : saved ? <Check size={16} /> : <Save size={16} />}
                  {isSaving ? t('education.syllabus.status.saving') : saved ? t('education.syllabus.status.published') : t('education.syllabus.status.publish')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ════════════════════ MEDIA MODE ════════════════════════ */}
      {mode === 'media' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', boxSizing: 'border-box' }}>
          {/* ── Two-tier visual explainer guide banner for Boss / HR / Teacher ── */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(99,102,241,0.08) 100%)',
            border: '1px solid rgba(14,165,233,0.25)', borderRadius: 18, padding: '20px 24px',
            display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Sparkles size={18} color="#38bdf8" />
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#f8fafc' }}>
                How Course Visuals Work in Sage AI
              </h4>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>
              Sage uses a <strong>two-tier visual hierarchy</strong> to maximize learner comprehension and retention:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 14 }}>
              <div style={{ background: 'rgba(15,23,42,0.6)', padding: '14px 16px', borderRadius: 14, border: '1px solid rgba(14,165,233,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>🎬</span>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: '#38bdf8' }}>Tier 1: Lesson Cover &amp; Scene Media</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#cbd5e1', lineHeight: 1.45 }}>
                  <strong>The Wide-Angle Hook.</strong> Shown right when the learner launches the lesson to set the physical context (e.g., photo of a dining room, espresso station, safety gear). Upload a photo or attach a video below.
                </p>
              </div>
              <div style={{ background: 'rgba(15,23,42,0.6)', padding: '14px 16px', borderRadius: 14, border: '1px solid rgba(139,92,246,0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>🎯</span>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: '#a78bfa' }}>Tier 2: Granular Concept Visuals</span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#cbd5e1', lineHeight: 1.45 }}>
                  <strong>The Exact Standard.</strong> Attached to individual concept pills (via the Syllabus tab). Sage AI displays these photos, GIFs, or video clips dynamically while explaining and quizzing that specific technique.
                </p>
              </div>
            </div>
          </div>

          {!generatedPlan ? (
            <div style={{ padding: 32, borderRadius: 16, border: '1px dashed rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.02)', textAlign: 'center' }}>
              <ImageIcon size={32} color="#475569" style={{ marginBottom: 10 }} />
              <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>No syllabus found. Generate and save a syllabus first, then return here to attach concept images.</p>
            </div>
          ) : (
            <>
              {/* Concept image gallery */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <h4 style={{ fontWeight: 800, color: '#f8fafc', margin: 0 }}>Concept Visuals &amp; Deep-Links</h4>
                    <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 0' }}>
                      {conceptImageCount > 0
                        ? `${conceptImageCount} concept${conceptImageCount !== 1 ? 's' : ''} have visuals attached · click any card to jump to it in the syllabus`
                        : 'No concept visuals yet — open the Syllabus tab and click + on any concept pill to attach photos, GIFs, or YouTube clips'}
                    </p>
                  </div>
                </div>

                {conceptImageCount > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                    {generatedPlan.flatMap(mod =>
                      mod.lessons.flatMap(les =>
                        les.core_concepts.map((concept, cIdx) => {
                          const key = conceptKey(mod.module_id, les.lesson_id, cIdx)
                          const visual = conceptImages[key]
                          if (!visual) return null
                          const isVid = visual.media_type === 'video'
                          const ytThumb = isVid ? getYouTubeThumbnail(visual.url) : null
                          return (
                            <div key={key} style={{ borderRadius: 14, overflow: 'hidden', background: '#0f172a', border: `1px solid ${isVid ? 'rgba(239,68,68,0.4)' : 'rgba(139,92,246,0.35)'}`, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                              <div style={{ position: 'relative' }}>
                                {ytThumb ? (
                                  <div style={{ position: 'relative', height: 140 }}>
                                    <img src={ytThumb} alt={visual.caption || concept} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} loading="lazy" />
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)' }}>
                                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(239,68,68,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Video size={16} color="#fff" />
                                      </div>
                                    </div>
                                  </div>
                                ) : isVid ? (
                                  <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.1)' }}>
                                    <Video size={32} color="#f87171" />
                                  </div>
                                ) : (
                                  <img src={visual.url} alt={visual.caption || concept} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} loading="lazy" />
                                )}
                                <div style={{ position: 'absolute', top: 6, left: 6 }}>
                                  <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 6, background: isVid ? 'rgba(239,68,68,0.85)' : 'rgba(99,102,241,0.85)', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {isVid ? 'Video' : 'Visual'}
                                  </span>
                                </div>
                                <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                                  <button onClick={() => jumpToConcept(key)} title="Edit in Syllabus tab" style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BookOpen size={13} /></button>
                                  <button onClick={() => openImageModal(mod.module_id, les.lesson_id, cIdx, concept)} title="Change visual" style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Upload size={13} /></button>
                                  <button onClick={() => saveConceptImage(key, '', '', 'image')} title="Remove visual" style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={13} /></button>
                                </div>
                              </div>
                              <div style={{ padding: '10px 12px' }}>
                                <p style={{ fontSize: 11, fontWeight: 800, color: isVid ? '#f87171' : '#a78bfa', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{concept}</p>
                                <p style={{ fontSize: 10, color: '#94a3b8', margin: '0 0 4px' }}>M{mod.module_id} · L{les.lesson_id}: {les.title}</p>
                                {visual.caption && <p style={{ fontSize: 11, color: '#cbd5e1', margin: 0, lineHeight: 1.4 }}>{visual.caption}</p>}
                              </div>
                            </div>
                          )
                        })
                      )
                    ).filter(Boolean)}
                  </div>
                )}
              </div>

              {/* Lesson-level approved media,  the full-class scan (AI-suggested + teacher-added) */}
              {lessonGallery.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <h4 style={{ fontWeight: 800, color: '#f8fafc', margin: 0, fontSize: 14 }}>Lesson Media ({lessonGallery.length})</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                    {lessonGallery.map(m => (
                      <div key={m.id} style={{ borderRadius: 14, overflow: 'hidden', background: '#0f172a', border: '1px solid rgba(14,165,233,0.3)' }}>
                        <div style={{ position: 'relative' }}>
                          {m.media_type === 'video' ? (
                            <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.8)' }}><Video size={28} color="#38bdf8" /></div>
                          ) : (
                            <img src={m.url} alt={m.caption || 'Lesson media'} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} loading="lazy" />
                          )}
                          <button
                            onClick={async () => {
                              const { error } = await supabase.from('lesson_media').delete().eq('id', m.id)
                              if (!error) setLessonGallery(prev => prev.filter(x => x.id !== m.id))
                            }}
                            title="Remove"
                            style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          ><X size={13} /></button>
                        </div>
                        <div style={{ padding: '10px 12px' }}>
                          <p style={{ fontSize: 10, color: '#94a3b8', margin: '0 0 2px' }}>M{m.lesson_unit} · L{m.lesson_number}</p>
                          <p style={{ fontSize: 11, color: '#cbd5e1', margin: 0, lineHeight: 1.4 }}>{m.caption || 'No caption'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 20 }}>
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontWeight: 800, color: '#f8fafc', margin: 0 }}>{t('education.syllabus.media.title')}</h4>
                    <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 0' }}>{t('education.syllabus.media.description')}</p>
                  </div>

                {approvedCount > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', marginBottom: 16, background: mediaSaved ? 'rgba(16,185,129,0.12)' : 'rgba(14,165,233,0.12)', borderRadius: 14, border: `1px solid ${mediaSaved ? 'rgba(16,185,129,0.3)' : 'rgba(14,165,233,0.3)'}`, flexWrap: 'wrap', gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: mediaSaved ? '#34d399' : '#38bdf8' }}>
                      {mediaSaved ? t('education.syllabus.media.saved') : t('education.syllabus.media.approvedX', { count: approvedCount })}
                    </span>
                    <button onClick={saveAllMedia} disabled={savingMedia || mediaSaved} style={{ padding: '10px 20px', borderRadius: 12, border: 'none', fontWeight: 800, fontSize: 13, background: mediaSaved ? '#059669' : 'linear-gradient(135deg, #0284c7, #0ea5e9)', color: '#fff', cursor: savingMedia || mediaSaved ? 'not-allowed' : 'pointer', boxShadow: '0 4px 14px rgba(14,165,233,0.3)' }}>
                      {savingMedia ? t('education.syllabus.status.saving') : mediaSaved ? t('education.syllabus.media.saved') : t('education.syllabus.media.saveFinish')}
                    </button>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {generatedPlan.map(module =>
                    module.lessons.map(lesson => {
                      const key = `${module.module_id}-${lesson.lesson_id}`
                      const suggestions = mediaSuggestions[key] || []
                      const ytQueries = youtubeQueries[key] || []
                      const isLoadingThis = loadingMedia === key
                      const approvedForLesson = suggestions.filter(s => s.approved).length

                      return (
                        <div key={key} style={{ background: '#131e33', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                          <div style={{ padding: 'clamp(12px,3vw,16px) clamp(12px,3vw,20px)', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                              <p style={{ fontWeight: 800, fontSize: 'clamp(13px,3.5vw,15px)', color: '#f8fafc', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>M{module.module_id} · L{lesson.lesson_id}: {lesson.title}</p>
                              <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lesson.core_concepts.slice(0, 4).join(' · ')}{lesson.core_concepts.length > 4 ? '…' : ''}</p>
                              {approvedForLesson > 0 && <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, color: '#34d399', fontSize: 10, fontWeight: 700 }}><Check size={10} strokeWidth={3} /> {approvedForLesson} media saved</div>}
                            </div>
                          </div>
                          <div style={{ padding: 'clamp(12px,3vw,16px) clamp(12px,3vw,20px)' }}>
                            {/* Teacher-added media only — AI suggestion display removed */}
                            {suggestions.filter(s => s.media_type === 'image' || s.media_type === 'video').length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
                                  {suggestions.filter(s => s.media_type === 'image').map((s, idx) => (
                                    <div key={idx} style={{ borderRadius: 12, overflow: 'hidden', border: `2px solid ${s.approved ? '#22c55e' : 'rgba(255,255,255,0.08)'}`, background: '#0f172a', position: 'relative' }}>
                                      <button onClick={() => removeMediaItem(key, suggestions.indexOf(s))} style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={13} /></button>
                                      <img src={s.url} alt={s.caption || 'Lesson visual'} style={{ width: '100%', height: 110, objectFit: 'cover' }} loading="lazy" />
                                      <div style={{ padding: '8px 10px' }}>
                                        <p style={{ fontSize: 11, fontWeight: 600, color: '#f8fafc', margin: '0 0 4px', lineHeight: 1.3 }}>{s.caption?.slice(0, 80) || 'Lesson Image'}</p>
                                        <button onClick={() => toggleApproval(key, suggestions.indexOf(s))} style={{ width: '100%', padding: '5px', borderRadius: 8, border: 'none', background: s.approved ? '#22c55e' : 'rgba(255,255,255,0.08)', color: s.approved ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>{s.approved ? '✓ Saved' : 'Mark as saved'}</button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {suggestions.filter(s => s.media_type === 'video').map((s, idx) => (
                                  <div key={`vid-${idx}`} style={{ marginBottom: 8, padding: '10px 14px', background: '#0f172a', borderRadius: 12, border: `2px solid ${s.approved ? '#22c55e' : 'rgba(255,255,255,0.08)'}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Video size={16} color="#f87171" /></div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <p style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.caption || 'Video resource'}</p>
                                      <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{s.source || 'YouTube'}</p>
                                    </div>
                                    <button onClick={() => removeMediaItem(key, suggestions.indexOf(s))} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }}><X size={16} /></button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {suggestions.filter(s => s.media_type === 'image' || s.media_type === 'video').length === 0 && (
                              <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px', fontStyle: 'italic' }}>No lesson media attached yet. Add an image or video URL below.</p>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8, paddingTop: 12, borderTop: '1px dashed rgba(255,255,255,0.12)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <Sparkles size={12} /> Add Lesson Cover &amp; Intro Media
                                </span>
                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                  Shown to learners at the start of this lesson
                                </span>
                              </div>

                              {/* Hidden file input for uploading cover photo directly */}
                              <input
                                type="file"
                                id={`lesson-cover-file-${key}`}
                                accept="image/*,.png,.jpg,.jpeg,.webp,.gif"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  const f = e.target.files?.[0]
                                  if (f) handleUploadLessonCover(key, f)
                                  e.target.value = ''
                                }}
                              />

                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                                <button
                                  type="button"
                                  onClick={() => document.getElementById(`lesson-cover-file-${key}`)?.click()}
                                  disabled={uploadingCoverKey === key}
                                  style={{
                                    padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(14,165,233,0.3)',
                                    background: 'rgba(14,165,233,0.12)', color: '#38bdf8', fontWeight: 700, fontSize: 12,
                                    cursor: uploadingCoverKey === key ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0
                                  }}
                                >
                                  {uploadingCoverKey === key ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                                  {uploadingCoverKey === key ? 'Uploading…' : 'Upload Cover Photo'}
                                </button>
                                <span style={{ fontSize: 12, color: '#64748b' }}>or</span>
                                <input type="url" value={manualImageUrls[key] || ''} onChange={(e) => setManualImageUrls(prev => ({ ...prev, [key]: e.target.value }))} placeholder="Paste Image URL…" style={{ flex: '1 1 140px', minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.7)', color: '#f8fafc', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                                <input type="text" value={manualImageCaptions[key] || ''} onChange={(e) => setManualImageCaptions(prev => ({ ...prev, [key]: e.target.value }))} placeholder="Caption (optional)" style={{ flex: '1 1 120px', minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.7)', color: '#f8fafc', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                                <button onClick={() => addManualImage(key)} disabled={!manualImageUrls[key]?.trim()} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 12, cursor: !manualImageUrls[key]?.trim() ? 'not-allowed' : 'pointer', opacity: !manualImageUrls[key]?.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}><ImageIcon size={14} /> Add Image</button>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' }}>
                                <input type="text" value={manualUrls[key] || ''} onChange={(e) => setManualUrls(prev => ({ ...prev, [key]: e.target.value }))} placeholder="Paste YouTube or Video URL…" style={{ flex: '1 1 180px', minWidth: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(15,23,42,0.7)', color: '#f8fafc', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                                <button onClick={() => addManualVideo(key)} disabled={!manualUrls[key]?.trim()} style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171', fontWeight: 700, fontSize: 12, cursor: !manualUrls[key]?.trim() ? 'not-allowed' : 'pointer', opacity: !manualUrls[key]?.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}><Video size={14} /> {t('education.media.addVideo')}</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ════════════════════ BOTTOM DRAWER: FULL LESSON DETAILS ════════════════════ */}
      {activeDrawerLesson && generatedPlan && (() => {
        const mIdx = activeDrawerLesson.mIdx
        const lIdx = activeDrawerLesson.lIdx
        const currMod = generatedPlan[mIdx]
        const currLes = currMod?.lessons?.[lIdx]
        if (!currMod || !currLes) return null

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 8500,
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: 0,
              boxSizing: 'border-box'
            }}
            onClick={() => setActiveDrawerLesson(null)}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 620,
                maxHeight: '88vh',
                background: 'linear-gradient(180deg, #131e33 0%, #0d1527 100%)',
                borderRadius: '24px 24px 0 0',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderBottom: 'none',
                boxShadow: '0 -16px 48px rgba(0, 0, 0, 0.8)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Grab Handle */}
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 6 }}>
                <div style={{ width: 44, height: 4, borderRadius: 2, background: 'rgba(255, 255, 255, 0.28)' }} />
              </div>

              {/* Drawer Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 18px 12px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                gap: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#38bdf8',
                    background: 'rgba(14, 165, 233, 0.15)',
                    padding: '3px 8px',
                    borderRadius: 6,
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}>
                    Unit {currMod.module_id} · L{currLes.lesson_id}
                  </span>
                  <span style={{
                    fontWeight: 800,
                    fontSize: 15,
                    color: '#f8fafc',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {currLes.title || 'Lesson Details'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setActiveDrawerLesson(null)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: 8,
                    color: '#f8fafc',
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5
                  }}
                >
                  <Check size={14} /> Done
                </button>
              </div>

              {/* Scrollable Drawer Body */}
              <div style={{
                padding: 'clamp(14px, 4vw, 22px)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                boxSizing: 'border-box'
              }}>
                {/* Lesson Title */}
                <div>
                  <label style={{ display: 'block', fontSize: 10.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.04em' }}>
                    Lesson Title
                  </label>
                  <input
                    value={currLes.title}
                    onChange={(e) => updateLessonField(mIdx, lIdx, 'title', e.target.value)}
                    placeholder="Lesson title"
                    style={{
                      width: '100%',
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: 10,
                      color: '#f8fafc',
                      fontWeight: 700,
                      fontSize: 15,
                      padding: '10px 14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Learning Objective */}
                <div>
                  <label style={{ display: 'block', fontSize: 10.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.04em' }}>
                    Learning Objective
                  </label>
                  <textarea
                    value={currLes.learning_objective}
                    onChange={(e) => updateLessonField(mIdx, lIdx, 'learning_objective', e.target.value)}
                    rows={3}
                    placeholder="Describe what the student will learn in this lesson..."
                    style={{
                      width: '100%',
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: 10,
                      color: '#cbd5e1',
                      fontSize: 13,
                      lineHeight: 1.5,
                      padding: '10px 14px',
                      outline: 'none',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Passing Score */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'rgba(15, 23, 42, 0.45)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 10,
                  padding: '10px 14px'
                }}>
                  <div>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#f8fafc', display: 'block' }}>Passing Score</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>Minimum mastery quiz score required to pass</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={currLes.passing_score}
                      onChange={(e) => updatePassingScore(mIdx, lIdx, e.target.value)}
                      style={{
                        width: 54,
                        height: 34,
                        textAlign: 'center',
                        borderRadius: 8,
                        border: '1px solid rgba(14, 165, 233, 0.35)',
                        background: '#0b1120',
                        color: '#38bdf8',
                        fontSize: 15,
                        fontWeight: 800,
                        outline: 'none'
                      }}
                    />
                    <span style={{ color: '#94a3b8', fontWeight: 700 }}>%</span>
                  </div>
                </div>

                {/* Core Concepts */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                    <label style={{ fontSize: 10.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Core Concepts ({currLes.core_concepts.length})
                    </label>
                    <span style={{ fontSize: 11, color: '#64748b' }}>Full text is displayed below</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                    {currLes.core_concepts.map((c, cIdx) => {
                      const imgKey = conceptKey(currMod.module_id, currLes.lesson_id, cIdx)
                      const visual = conceptImages[imgKey]
                      const hasVisual = !!visual
                      const isVid = visual?.media_type === 'video'

                      return (
                        <div
                          key={cIdx}
                          id={`drawer-concept-${imgKey}`}
                          style={{
                            background: hasVisual
                              ? (isVid ? 'rgba(239, 68, 68, 0.1)' : 'rgba(99, 102, 241, 0.1)')
                              : 'rgba(15, 23, 42, 0.65)',
                            border: `1px solid ${hasVisual ? (isVid ? 'rgba(239, 68, 68, 0.35)' : 'rgba(139, 92, 246, 0.35)') : 'rgba(255, 255, 255, 0.08)'}`,
                            borderRadius: 12,
                            padding: '10px 12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            boxSizing: 'border-box'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 800,
                              color: hasVisual ? (isVid ? '#f87171' : '#a78bfa') : '#38bdf8',
                              background: hasVisual ? (isVid ? 'rgba(239, 68, 68, 0.18)' : 'rgba(139, 92, 246, 0.18)') : 'rgba(14, 165, 233, 0.14)',
                              padding: '2px 8px',
                              borderRadius: 6
                            }}>
                              Concept #{cIdx + 1}
                            </span>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {/* Attach visual button - calls existing openImageModal */}
                              <button
                                type="button"
                                onClick={() => openImageModal(currMod.module_id, currLes.lesson_id, cIdx, c)}
                                title={hasVisual ? (isVid ? 'Video attached — click to edit' : 'Visual attached — click to edit') : 'Attach photo, GIF or video to this concept'}
                                style={{
                                  background: hasVisual ? (isVid ? 'rgba(239, 68, 68, 0.22)' : 'rgba(139, 92, 246, 0.22)') : 'rgba(14, 165, 233, 0.14)',
                                  border: `1px solid ${hasVisual ? (isVid ? 'rgba(239, 68, 68, 0.4)' : 'rgba(139, 92, 246, 0.4)') : 'rgba(14, 165, 233, 0.28)'}`,
                                  color: hasVisual ? (isVid ? '#f87171' : '#a78bfa') : '#38bdf8',
                                  borderRadius: 6,
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                              >
                                {hasVisual ? (isVid ? <><Video size={12} /> Video Attached</> : <><Check size={12} strokeWidth={3} /> Visual Attached</>) : <><Plus size={11} strokeWidth={3} /> Attach Media</>}
                              </button>

                              <button
                                type="button"
                                onClick={() => removeConcept(mIdx, lIdx, cIdx)}
                                title="Remove concept"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#64748b',
                                  fontSize: 16,
                                  fontWeight: 900,
                                  cursor: 'pointer',
                                  padding: '2px 6px'
                                }}
                              >
                                ×
                              </button>
                            </div>
                          </div>

                          {/* Full auto-wrapping textarea */}
                          <textarea
                            value={c}
                            onChange={(e) => updateConceptField(mIdx, lIdx, cIdx, e.target.value)}
                            rows={Math.max(2, Math.min(6, Math.ceil((c?.length || 1) / 38)))}
                            placeholder="Enter full concept statement or takeaway..."
                            style={{
                              width: '100%',
                              background: 'transparent',
                              border: 'none',
                              outline: 'none',
                              color: '#f8fafc',
                              fontSize: 13,
                              lineHeight: 1.5,
                              resize: 'none',
                              padding: 0,
                              margin: 0,
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      )
                    })}

                    <button
                      type="button"
                      onClick={() => addConcept(mIdx, lIdx)}
                      style={{
                        alignSelf: 'flex-start',
                        fontSize: 12,
                        background: 'rgba(14, 165, 233, 0.08)',
                        border: '1px dashed rgba(14, 165, 233, 0.35)',
                        color: '#38bdf8',
                        borderRadius: 8,
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <Plus size={13} /> Add concept
                    </button>
                  </div>
                </div>
              </div>

              {/* Drawer Sticky Footer */}
              <div style={{
                padding: '12px 18px',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                background: 'rgba(15, 23, 42, 0.8)',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setActiveDrawerLesson(null)}
                  style={{
                    width: '100%',
                    padding: '11px',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #0284c7, #0ea5e9)',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    boxShadow: '0 4px 14px rgba(14, 165, 233, 0.3)'
                  }}
                >
                  <Check size={16} /> Save &amp; Close Drawer
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Concept Visual Modal */}
      {imageModal && (
        <ConceptImageModal
          classId={classId}
          concept={imageModal.concept}
          existing={conceptImages[imageModal.key] || null}
          onSave={(url, caption, mediaType) => saveConceptImage(imageModal.key, url, caption, mediaType)}
          onClose={() => setImageModal(null)}
        />
      )}
    </div>
  )
}
